import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Typography, Tooltip, Spin, Alert, Tag } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { calculateLevinePhenoAge, calculateAdjustedBiologicalAge } from '../utils/riskCalculators';
import { calculateAverageAbsoluteZScore } from '../utils/scoringEngineV2';
import useHealthSnapshotData from '../hooks/useHealthSnapshotData';
import { getCalculatorSettings } from '../services/configService';

const { Title, Text } = Typography;

const AdjustmentLineItem = ({ label, value, tooltip, isSubtotal = false, isFinal = false }) => (
  <Row justify="space-between" align="middle" style={{ padding: isFinal ? '12px 0' : '6px 0', borderTop: isSubtotal || isFinal ? '1px solid #f0f0f0' : 'none', marginTop: isSubtotal ? '8px' : '0' }}>
    <Col>
      <Text strong={isSubtotal || isFinal} style={{ fontSize: isFinal ? '18px' : '16px' }}>
        {label}
        {tooltip && (
          <Tooltip title={tooltip}>
            <QuestionCircleOutlined style={{ marginLeft: '8px', color: '#8c8c8c' }} />
          </Tooltip>
        )}
      </Text>
    </Col>
    <Col>
      <Text strong={isSubtotal || isFinal} style={{ fontSize: isFinal ? '18px' : '16px', color: value > 0 ? '#cf1322' : '#3f8600' }}>
        {value > 0 ? '+' : ''}{value.toFixed(2)}
      </Text>
    </Col>
  </Row>
);

const BiologicalAgeBreakdown = ({ patientId }) => {
  const [calculatorSettings, setCalculatorSettings] = useState(null);
  const [settingsLoading, setSettingsLoading] = useState(true);

  const {
    loading,
    error: hookError,
    tableData,
    systemDeviations,
    lifestyleDeviations,
    patientDemographics,
    overallDeviation,
  } = useHealthSnapshotData(patientId);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const settings = await getCalculatorSettings();
        setCalculatorSettings(settings);
      } catch (error) {
        console.error('[BiologicalAgeBreakdown] Error fetching calculator settings:', error);
        // Default to adjusted if settings can't be fetched
        setCalculatorSettings({ biologicalAgeMethod: 'adjusted' });
      } finally {
        setSettingsLoading(false);
      }
    };
    fetchSettings();
  }, []);

  if (loading || settingsLoading) {
    return <Spin tip="Calculating Biological Age..." />;
  }

  if (hookError) {
    return <Alert message="Error loading biological age data" description={hookError} type="error" showIcon />;
  }

  if (!tableData || tableData.length === 0 || !patientDemographics || !systemDeviations) {
    return <Text type="secondary">Awaiting data for Biological Age calculation...</Text>;
  }

  // 1. Calculate PhenoAge
  const getParameterValue = (paramKey) => {
    for (const categoryGroup of tableData) {
      const param = (categoryGroup.parameters || []).find(p => p.key === paramKey);
      if (param && param.currentValue !== null && param.currentValue !== undefined) {
        const numValue = parseFloat(param.currentValue);
        return isNaN(numValue) ? null : numValue;
      }
    }
    return null;
  };

  const phenoAgeParams = {
    chronologicalAge: patientDemographics.age,
    labs_protein_albumin: getParameterValue('labs_protein_albumin'),
    labs_renal_creatinine: getParameterValue('labs_renal_creatinine'),
    labs_glucose_glucose: getParameterValue('labs_glucose_glucose'),
    labs_inflammation_hscrp: getParameterValue('labs_inflammation_hscrp'),
    labs_cbc_lymph_pct: getParameterValue('labs_cbc_lymph_pct'),
    labs_cbc_mcv: getParameterValue('labs_cbc_mcv'),
    labs_cbc_rdw: getParameterValue('labs_cbc_rdw'),
    labs_hepatology_alp: getParameterValue('labs_hepatology_alp'),
    labs_cbc_wbc: getParameterValue('labs_cbc_wbc'),
  };

  const phenoAgeResult = calculateLevinePhenoAge(phenoAgeParams);

  if (phenoAgeResult.error) {
    return <Alert message="Cannot Calculate Biological Age" description={phenoAgeResult.error} type="warning" showIcon />;
  }

  // 2. Calculate PhenoAge Panel Z-Score
  const phenoAgeBiomarkerKeys = Object.keys(phenoAgeParams).filter(k => k !== 'chronologicalAge');
  const phenoAgeBiomarkers = tableData.flatMap(g => g.parameters).filter(p => phenoAgeBiomarkerKeys.includes(p.key));
  const phenoAgePanelZScoreResult = calculateAverageAbsoluteZScore(phenoAgeBiomarkers);

  // Determine which method to use
  const isPhenoAgeOnly = calculatorSettings?.biologicalAgeMethod === 'phenoage';

  // 3. Calculate Adjusted Biological Age (only if not PhenoAge-only mode)
  let adjustedAgeResult, adjustedBiologicalAge, ageAdjustment, systemAdjustmentDetails;
  let biomarkerAdjustment, healthAdjustedAge, totalSystemAdjustment;

  if (!isPhenoAgeOnly) {
    const adjustmentParams = {
      phenoAge: phenoAgeResult.phenoAge,
      overallZScore: overallDeviation.average,
      phenoAgePanelZScore: phenoAgePanelZScoreResult.averageAbsoluteZScore,
      systemDeviations,
      lifestyleDeviations,
    };

    adjustedAgeResult = calculateAdjustedBiologicalAge(adjustmentParams);

    if (adjustedAgeResult.error) {
      return <Alert message="Cannot Calculate Adjusted Biological Age" description={adjustedAgeResult.error} type="warning" showIcon />;
    }

    ({ adjustedBiologicalAge, ageAdjustment, systemAdjustmentDetails } = adjustedAgeResult);
    biomarkerAdjustment = ageAdjustment - (adjustedAgeResult.synergisticResults?.totalSynergisticAdjustment || 0) - systemAdjustmentDetails.reduce((sum, item) => sum + item.penalty, 0);
    healthAdjustedAge = phenoAgeResult.phenoAge + biomarkerAdjustment;
    totalSystemAdjustment = ageAdjustment - biomarkerAdjustment;
  }

  // Determine display values based on method
  const displayBiologicalAge = isPhenoAgeOnly ? phenoAgeResult.phenoAge : adjustedBiologicalAge;
  const displayAgeDifference = displayBiologicalAge - patientDemographics.age;
  const displayColor = displayAgeDifference < -2 ? '#52c41a' :
                      displayAgeDifference < 0 ? '#73d13d' :
                      displayAgeDifference > 2 ? '#f5222d' : '#faad14';
  const methodLabel = isPhenoAgeOnly ? 'PhenoAge - Validated' : 'Adjusted - Proprietary';
  const methodBadge = isPhenoAgeOnly ?
    <Tag color="green">✓ Validated</Tag> :
    <Tag color="orange">Proprietary</Tag>;

  return (
    <Card>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <Title level={4} style={{ margin: 0 }}>Biological Age Breakdown</Title>
        {methodBadge}
      </div>

      <Row justify="center" style={{ marginBottom: '24px' }}>
        <Col>
            <Text style={{ fontSize: '36px', fontWeight: 'bold', color: displayColor }}>
                {displayBiologicalAge.toFixed(2)}
            </Text>
            <br />
            <Text type="secondary" style={{ textAlign: 'center', display: 'block' }}>
                {methodLabel}
            </Text>
        </Col>
      </Row>

      {isPhenoAgeOnly ? (
        // PhenoAge Only Mode - Show only baseline calculation
        <Card type="inner" title="PhenoAge Calculation (Levine et al. 2018)">
          <Alert
            message="Research-Validated Method"
            description="This calculation uses only the 9-biomarker PhenoAge algorithm validated against mortality outcomes in multiple independent cohorts."
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />
          <Row justify="space-between" align="middle" style={{ padding: '12px 0' }}>
            <Col>
              <Text strong style={{ fontSize: '16px' }}>Chronological Age</Text>
            </Col>
            <Col>
              <Text strong style={{ fontSize: '16px' }}>
                {patientDemographics.age.toFixed(2)} years
              </Text>
            </Col>
          </Row>
          <Row justify="space-between" align="middle" style={{ padding: '12px 0', borderTop: '1px solid #f0f0f0' }}>
            <Col>
              <Text strong style={{ fontSize: '18px' }}>PhenoAge (Biological Age)</Text>
            </Col>
            <Col>
              <Text strong style={{ fontSize: '18px', color: displayColor }}>
                {phenoAgeResult.phenoAge.toFixed(2)} years
              </Text>
            </Col>
          </Row>
          <Row justify="space-between" align="middle" style={{ padding: '12px 0', borderTop: '1px solid #f0f0f0' }}>
            <Col>
              <Text strong style={{ fontSize: '16px' }}>Age Difference</Text>
            </Col>
            <Col>
              <Text strong style={{ fontSize: '16px', color: displayColor }}>
                {displayAgeDifference > 0 ? '+' : ''}{displayAgeDifference.toFixed(2)} years
              </Text>
            </Col>
          </Row>
        </Card>
      ) : (
        // Adjusted Biological Age Mode - Show full breakdown
        <>
          <Card type="inner" title="Calculation Details">
            <AdjustmentLineItem label="Baseline PhenoAge" value={phenoAgeResult.phenoAge} isSubtotal={true} />
            <AdjustmentLineItem
              label="Overall Health Adjustment"
              value={biomarkerAdjustment}
              tooltip="Adjusts age based on how your overall health (all biomarkers) compares to the standard 9-marker panel."
            />
            <AdjustmentLineItem label="Health-Adjusted Age" value={healthAdjustedAge} isSubtotal={true} />
            <AdjustmentLineItem
              label="System & Synergy Adjustment"
              value={totalSystemAdjustment}
              tooltip="The combined impact of your individual health systems and any amplified effects from their interactions."
            />
            <AdjustmentLineItem label="Final Biological Age" value={adjustedBiologicalAge} isFinal={true} />
          </Card>

          <Title level={5} style={{ marginTop: '24px' }}>Health System Contributions</Title>
          <Row gutter={[16, 16]}>
            {systemAdjustmentDetails.map(sys => (
              <Col key={sys.system} xs={12} sm={8} md={6} style={{ textAlign: 'center' }}>
                <Tooltip title={`Score: ${sys.score.toFixed(1)}%`}>
                  <div>
                    {/* Placeholder for Circular Graph */}
                    <div style={{
                      width: '80px',
                      height: '80px',
                      borderRadius: '50%',
                      border: '4px solid #f0f0f0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto',
                      borderColor: sys.penalty > 0 ? '#ff7875' : '#95de64'
                    }}>
                      <Text strong style={{ fontSize: '16px', color: sys.penalty > 0 ? '#cf1322' : '#3f8600' }}>
                        {sys.penalty > 0 ? '+' : ''}{sys.penalty.toFixed(2)}
                      </Text>
                    </div>
                    <Text type="secondary">{sys.system.charAt(0).toUpperCase() + sys.system.slice(1)}</Text>
                  </div>
                </Tooltip>
              </Col>
            ))}
            {(adjustedAgeResult.synergisticResults?.totalSynergisticAdjustment !== 0) && (
              <Col key="synergy" xs={12} sm={8} md={6} style={{ textAlign: 'center' }}>
                <Tooltip title={adjustedAgeResult.synergisticResults?.appliedEffects?.map(e => e.details).join(', ') || 'Synergistic effects'}>
                  <div>
                    {/* Placeholder for Circular Graph */}
                    <div style={{
                      width: '80px',
                      height: '80px',
                      borderRadius: '50%',
                      border: '4px solid #f0f0f0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto',
                      borderColor: (adjustedAgeResult.synergisticResults?.totalSynergisticAdjustment || 0) > 0 ? '#ff7875' : '#95de64'
                    }}>
                      <Text strong style={{ fontSize: '16px', color: (adjustedAgeResult.synergisticResults?.totalSynergisticAdjustment || 0) > 0 ? '#cf1322' : '#3f8600' }}>
                        {(adjustedAgeResult.synergisticResults?.totalSynergisticAdjustment || 0) > 0 ? '+' : ''}{(adjustedAgeResult.synergisticResults?.totalSynergisticAdjustment || 0).toFixed(2)}
                      </Text>
                    </div>
                    <Text type="secondary">Synergy Effects</Text>
                  </div>
                </Tooltip>
              </Col>
            )}
          </Row>
        </>
      )}
    </Card>
  );
};

export default BiologicalAgeBreakdown;
